'use client'

import { useSignal, useSignals } from '@preact/signals-react/runtime'
import Icon from '@susu/headless-commerce/components/Icon/Icon'
import { ProgressBar } from '@susu/headless-commerce/components/ProgressBar/ProgressBar'
import { cartSegmentProducts } from '@susu/headless-commerce/contexts/cart'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { ClientFinishButton } from '@susu/headless-commerce/features/configurator/components/Navigation/ClientFinishButton'
import styles from '@susu/headless-commerce/features/configurator/components/Navigation/Navigation.module.scss'
import { configuratorSignal } from '@susu/headless-commerce/features/configurator/signals/configurator'
import { trackNextStepClickEvent } from '@susu/headless-commerce/features/configurator/utils/trackNextStepClickEvent'
import { trackOptionConfirm } from '@susu/headless-commerce/features/configurator/utils/trackOptionConfirm'
import { trackPageLoadedEvent } from '@susu/headless-commerce/features/configurator/utils/trackPageLoadedEvent'
import { trackPreviousStepClickedEvent } from '@susu/headless-commerce/features/configurator/utils/trackPreviousStepClickedEvent'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import log from '@susu/headless-commerce/utils/log'
import { useI18N } from '@susu/i18n/client'
import { isUndefined } from '@susu/undefined'
import classNames from 'classnames'
import { useRouter } from 'next/navigation'
import type { ReactNode } from 'react'
import { useCallback } from 'react'

export type ClientNavigationProps = {
  children: ReactNode
  summaryURL: string
}

export const ClientNavigation = ({
  children,
  summaryURL,
}: Readonly<ClientNavigationProps>) => {
  useSignals()

  const configurator = configuratorSignal.value
  const router = useRouter()
  const country = useSignal(useCountry())
  const locale = useSignal(useLocale())
  const pageType = useSignal(usePageType())
  const { t } = useI18N()

  const handleNextStepClick = useCallback(async () => {
    log.debug({
      method: 'handleNextStepClick',
    })

    if (isUndefined(configurator.selectedOption.value)) {
      throw new Error('ClientNavigation: No selected option')
    }

    await trackOptionConfirm({
      configurator,
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
      option: configurator.selectedOption.value,
    })

    // Track next step
    if (isUndefined(configurator.currentStep.value)) {
      return
    }
    await trackNextStepClickEvent({
      categoryStep: configurator.currentStep.value.name.value,
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
    })

    await configurator.navigateToNextStep()

    const url = new URL(window.location.toString())
    url.searchParams.set('step', configurator.currentStep.value.name.value)
    const urlAsString = decodeURIComponent(url.toString())
    window.history.pushState({}, '', urlAsString)

    log.debug({
      method: 'handleNextStepClick',
      phase: 'after navigateToNextStep',
      currentStepName: configurator.currentStep.value.name.value,
    })
    // Track page loaded
    if (isUndefined(cartSegmentProducts.value)) {
      return
    }
    await trackPageLoadedEvent({
      categoryStep: configurator.currentStep.value.name.value,
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
      segmentProducts: cartSegmentProducts.value,
    })
  }, [
    configurator,
    configurator.selectedOption.value,
    country.value,
    locale.value,
    pageType.value,
    // router.push,
  ])

  const handlePreviousStepClick = useCallback(async () => {
    if (isUndefined(configurator.selectedOption.value)) {
      throw new Error('ClientNavigation: No selected option')
    }

    if (isUndefined(configurator.currentStep.value)) {
      throw new Error('ClientNavigation: No current step')
    }

    await trackPreviousStepClickedEvent({
      categoryStep: configurator.currentStep.value.name.value,
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
    })

    await configurator.navigateToPreviousStep()

    const url = new URL(window.location.toString())
    url.searchParams.set('step', configurator.currentStep.value.name.value)
    const urlAsString = decodeURIComponent(url.toString())
    window.history.pushState({}, '', urlAsString)

    // Track page loaded
    if (isUndefined(cartSegmentProducts.value)) {
      throw new Error('ClientNavigation: No cart products')
    }
    await trackPageLoadedEvent({
      categoryStep: configurator.currentStep.value.name.value,
      country: country.value,
      locale: locale.value,
      pageType: pageType.value,
      segmentProducts: cartSegmentProducts.value,
    })
  }, [
    configurator,
    configurator.selectedOption.value,
    country.value,
    locale.value,
    pageType.value,
  ])

  const navigationControlClasses = classNames(
    styles.navigation__control,
    styles['navigation__control-previous'],
    {
      [styles['navigation__control--is-disabled']]:
        configurator.currentStepIndex.value === 0,
    },
  )
  const navigationControlTextClasses = classNames(
    'body-small-light',
    styles.navigation__control__text,
  )
  const navigationTitleClasses = classNames(
    styles.navigation__title,
    'body-small-medium-sm title-03-medium-lg',
  )
  const navigationControlNextClasses = classNames(
    styles.navigation__control,
    styles['navigation__control-next'],
    {
      [styles.navigation__control__last]:
        configurator.isCurrentStepTheLastStep(),
    },
  )

  return (
    <div className={styles.navigation} data-testid="configurator_navigation">
      <div className={styles.navigation__progress}>
        <ProgressBar
          step={configurator.currentStepIndex.value + 1}
          steps={configurator.steps.value.length}
        />
      </div>

      <div className={navigationControlClasses}>
        <button
          type="button"
          className={styles.navigation__control__content}
          onClick={handlePreviousStepClick}
          disabled={
            configurator.isCurrentStepTheFirstStep() ||
            configurator.isOneStepOutOfStock()
          }
          data-testid="button_configurator_previous"
        >
          <span className={styles.navigation__control__icon}>
            <Icon icon="arrow_left" />
          </span>
          <span className={navigationControlTextClasses}>
            {t('configurator.back')}
          </span>
        </button>
      </div>
      <div
        className={navigationTitleClasses}
        data-testid="configurator_step_title"
      >
        {configurator.currentStep.value?.title.value}
      </div>

      {configurator.isCurrentStepTheLastStep() ? (
        <ClientFinishButton summaryURL={summaryURL} />
      ) : (
        <div className={navigationControlNextClasses}>
          <button
            type="button"
            className={styles.navigation__control__content}
            onClick={handleNextStepClick}
            disabled={
              configurator.isCurrentStepTheLastStep() ||
              configurator.isOneStepOutOfStock()
            }
            data-testid="button_configurator_next"
          >
            <span className={styles.navigation__control__icon}>
              <Icon icon={'arrow_right'} />
            </span>
            <span className={navigationControlTextClasses}>
              {t('configurator.next')}
            </span>
          </button>
        </div>
      )}
      <div className={styles['navigation__options-list']}>{children}</div>
    </div>
  )
}
